import { extend } from 'lodash'
import { createSlice } from '@reduxjs/toolkit'
import {
	ckeditorStyles,
	ckeditorCss,
} from '../../../utils/ckeditorStylingDeffaults.js'

import {
	selectForm,
	addField,
	removeField,
	validField,
	selectStep,
	addStep,
	removeStep,
	selectEdit,
	addVariable,
	removeVariable,
	selectVariable,
	move,
	movePage,
	addPage,
} from './selectors'

const initialState = {
	data: {
		id: 1,
		title: '',
		form: [
			{
				title: '',
				fields: [],
				ids: [],
				valid: [],
			},
		],
		workflow: {
			steps: [],
		},
		text: '',
		isFile: false,
		signers: {
			signers: [],
			message: '',
			responsible_group: [],
			icp_signing: false,
			signing_order: false,
		},
		variables: [[]],
		config: {
			auto_title: {
				enabled: false,
			},
			external_user: {
				enabled: false,
			},
			counter: 0,
			output: 'docx',
			templateType: '.txt',
			contextAi: false,
			allowAddendums: false,
			hasPreview: false,
		},
		team_id: '',
		ckeditorStyles: ckeditorStyles,
		ckeditorCss: ckeditorCss,
	},
	groupsUsers: {
		loading: false,
		data: {},
	},
	error: null,
	loading: false,
	docPosted: false,
}

const { actions, reducer } = createSlice({
	name: 'post',
	initialState,
	reducers: {
		editTemplateTitle: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					title: payload.title,
				}),
			}),
		resetTemplateState: () => initialState,
		getTemplateDetail: (state) =>
			extend(state, {
				loading: true,
			}),
		getTemplateDetailSuccess: (state, { payload }) =>
			extend(state, {
				data: selectEdit(state.data, payload),
				loading: false,
				docPosted: true,
			}),
		getTemplateDetailFailure: (state, { payload }) =>
			extend(state, {
				error: payload.error,
				loading: false,
			}),
		editTemplateFormInfo: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					form: selectForm(state.data.form, payload),
					variables: selectVariable(state.data.variables, payload),
				}),
			}),
		editTemplatePageAdd: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					form: addPage(
						state.data.form,
						payload.newPage,
						payload.currentIndex,
						initialState.data.form
					),
					variables: [...state.data.variables, []],
				}),
			}),
		editTemplatePageRemove: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					form: state.data.form.filter(
						(page, index) => index !== payload.pageIndex
					),
					variables: state.data.variables.filter(
						(page, index) => index !== payload.pageIndex
					),
				}),
			}),
		editTemplatePageMove: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					form: movePage(state.data.form, payload),
					variables: movePage(state.data.variables, payload),
				}),
			}),
		editTemplateFieldAdd: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					form: addField(state.data.form, payload),
					variables: addVariable(state.data.variables, payload),
				}),
			}),
		editTemplateFieldRemove: (state, { payload }) => {
			return extend(state, {
				data: extend(state.data, {
					form: removeField(state.data.form, payload),
					variables: removeVariable(state.data.variables, payload),
				}),
			})
		},
		editTemplateFieldValid: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					form: validField(state.data.form, payload),
				}),
			}),
		editTemplateStepInfo: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					workflow: selectStep(state.data.workflow, payload),
				}),
			}),
		editTemplateStepAdd: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					workflow: addStep(state.data.workflow, payload),
				}),
			}),
		editTemplateStepRemove: (state, { payload }) =>
			extend(state, {
				workflow: removeStep(state.data.workflow, payload),
			}),
		editTemplateSignersInfo: (state, { payload }) => {
			const { updatedSigningParties } = payload
			return extend(state, {
				data: extend(state.data, {
					signers: updatedSigningParties,
				}),
			})
		},
		editTemplateMove: (state, { payload }) =>
			extend(state, {
				data: move(state.data, payload),
			}),
		editTemplateText: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					text: payload.value,
				}),
			}),
		editTemplateCkeditorCss: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					ckeditorCss: payload.value,
				}),
			}),
		editTemplateCkeditorStyles: (state, { payload }) =>
			extend(state, {
				data: extend(state.data, {
					ckeditorStyles: payload.value,
				}),
			}),
		editTemplateRequest: (state) =>
			extend(state, {
				loading: true,
			}),
		editTemplateSuccess: (state) =>
			extend(state, {
				loading: false,
				docPosted: true,
			}),
		editTemplateFailure: (state, { payload }) =>
			extend(state, {
				loading: false,
				error: payload.error,
			}),
		getTemplateDownload: (state) =>
			extend(state, {
				loading: true,
			}),
		getTemplateDownloadSuccess: (state) =>
			extend(state, {
				loading: false,
			}),
		getTemplateDownloadFailure: (state, { payload }) =>
			extend(state, {
				error: payload.error,
				loading: false,
			}),
		setDocPosted: (state, { payload }) =>
			extend(state, {
				docPosted: payload,
			}),
		getGroupsUsers: (state, { payload }) => {
			const { groupsUsers } = state
			groupsUsers.loading = true
			extend(state, {
				groupsUsers,
			})
		},
		getGroupsUsersSuccess: (state, { payload }) => {
			const { groupId, data } = payload
			const { groupsUsers } = state
			groupsUsers.data[groupId] = data.users
			groupsUsers.loading = false
			extend(state, { groupsUsers })
		},
		getGroupsUsersFailure: (state) => {
			const { groupsUsers } = state
			groupsUsers.loading = false
			extend(state, { groupsUsers })
		},
		onChangeConfig: (state, { payload }) => {
			const { team_id, ...config } = payload
			extend(state, {
				data: extend(state.data, {
					config,
					team_id,
				}),
			})
		},
		updateTemplateType: (state, { payload }) => {
			extend(state.data.config, {
				templateType: payload,
			})
		},
		updateTemplateTeam: (state, { payload }) => {
			extend(state.data, {
				team_id: payload,
			})
		},
		updatecontextAi: (state, { payload }) => {
			extend(state.data.config, {
				contextAi: payload,
			})
		},
	},
})

export const {
	editTemplateTitle,
	resetTemplateState,
	getTemplateDetail,
	getTemplateDetailSuccess,
	getTemplateDetailFailure,
	editTemplateFormInfo,
	editTemplatePageAdd,
	editTemplatePageRemove,
	editTemplatePageMove,
	editTemplateFieldAdd,
	editTemplateFieldRemove,
	editTemplateFieldValid,
	editTemplateStepInfo,
	editTemplateStepAdd,
	editTemplateStepRemove,
	editTemplateSignersInfo,
	editTemplateText,
	editTemplateCkeditorCss,
	editTemplateCkeditorStyles,
	editTemplateRequest,
	editTemplateSuccess,
	editTemplateFailure,
	editTemplateMove,
	getTemplateDownload,
	getTemplateDownloadSuccess,
	getTemplateDownloadFailure,
	setDocPosted,
	getGroupsUsers,
	getGroupsUsersSuccess,
	getGroupsUsersFailure,
	onChangeConfig,
	updateTemplateType,
	updateTemplateTeam,
	updatecontextAi,
} = actions

export { default as editTemplateSaga } from './sagas'

export default reducer
